import React, { useState, useEffect, useMemo } from 'react';
import { useRouter } from '../../router/Router';
import { demandRequestService } from '../../services/demandRequestService';
import { DemandRequest, DemandRequestStatus, TripType } from '../../types';
import { SEED_UNIVERSITIES } from '../../constants/mockSeed';
import {
  Send,
  Search,
  Clock,
  CheckCircle2,
  XCircle,
  Phone,
  MessageSquare,
  Sparkles,
  ArrowRight,
  TrendingUp,
  RefreshCw,
  Plus,
  X,
  Trash2,
  Calendar,
  MapPin,
  BarChart2,
  Copy,
  Check,
} from 'lucide-react';
import { AdminLoadMore } from './AdminLoadMore';
import './AdminPages.css';

const ALEXANDRIA_PICKUP_POINTS = [
  'الموقف الجديد بمحرم بيك',
  'الكيلو 21',
];

export const AdminDemandRequests: React.FC = () => {
  const { navigate } = useRouter();
  const [requests, setRequests] = useState<DemandRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [uniFilter, setUniFilter] = useState<string>('ALL');
  const [tripTypeFilter, setTripTypeFilter] = useState<string>('ALL');
  const [visibleCount, setVisibleCount] = useState<number>(15);

  // Action status message
  const [actionMessage, setActionMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Link to Trip Modal
  const [selectedReqForTrip, setSelectedReqForTrip] = useState<DemandRequest | null>(null);
  const [tripCodeInput, setTripCodeInput] = useState('');

  // New Request Modal (Admin Manual Entry)
  const [showNewRequestModal, setShowNewRequestModal] = useState(false);
  const [newStudentName, setNewStudentName] = useState('');
  const [newStudentPhone, setNewStudentPhone] = useState('');
  const [newUniId, setNewUniId] = useState(SEED_UNIVERSITIES[2]?.id || 'uni_tanta');
  const [newDate, setNewDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 2);
    return d.toISOString().split('T')[0];
  });
  const [newDepartureTime, setNewDepartureTime] = useState('06:30');
  const [newTripType, setNewTripType] = useState<TripType>('ROUND_TRIP');
  const [newPickupLocation, setNewPickupLocation] = useState(ALEXANDRIA_PICKUP_POINTS[0]);
  const [newNotes, setNewNotes] = useState('');

  // Copied state indicator
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const loadRequests = async () => {
    setIsLoading(true);
    try {
      const data = await demandRequestService.getAllDemandRequests();
      setRequests(data);
    } catch (err) {
      console.warn('Error loading demand requests for admin:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  // Filtered requests
  const filteredRequests = useMemo(() => {
    return requests.filter((r) => {
      const matchSearch =
        searchTerm === '' ||
        r.requestCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.studentSnapshot?.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.studentSnapshot?.phoneNumber?.includes(searchTerm) ||
        r.universityName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.pickupLocation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.notes?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchStatus = statusFilter === 'ALL' || r.status === statusFilter;
      const matchUni = uniFilter === 'ALL' || r.universityName === uniFilter;
      const matchType = tripTypeFilter === 'ALL' || r.tripType === tripTypeFilter;

      return matchSearch && matchStatus && matchUni && matchType;
    });
  }, [requests, searchTerm, statusFilter, uniFilter, tripTypeFilter]);

  // Reset pagination on filter change
  useEffect(() => {
    setVisibleCount(15);
  }, [searchTerm, statusFilter, uniFilter, tripTypeFilter]);

  const visibleRequests = useMemo(() => {
    return filteredRequests.slice(0, visibleCount);
  }, [filteredRequests, visibleCount]);


  // Unique universities list for filter dropdown
  const universitiesList = useMemo(() => {
    const set = new Set<string>();
    requests.forEach((r) => {
      if (r.universityName) set.add(r.universityName);
    });
    // Add seed universities if list is small
    SEED_UNIVERSITIES.forEach((u) => set.add(u.name));
    return Array.from(set);
  }, [requests]);

  // KPIs calculation
  const stats = useMemo(() => {
    const total = requests.length;
    const pending = requests.filter((r) => r.status === 'PENDING').length;
    const reviewed = requests.filter((r) => r.status === 'REVIEWED').length;
    const created = requests.filter((r) => r.status === 'TRIP_CREATED').length;
    return { total, pending, reviewed, created };
  }, [requests]);

  // Route clusters: group pending/reviewed requests by (universityName + date)
  const routeClusters = useMemo(() => {
    const clusters: Record<string, { university: string; date: string; count: number; requests: DemandRequest[] }> = {};

    requests
      .filter((r) => r.status === 'PENDING' || r.status === 'REVIEWED')
      .forEach((r) => {
        const key = `${r.universityName}__${r.date}`;
        if (!clusters[key]) {
          clusters[key] = {
            university: r.universityName,
            date: r.date,
            count: 0,
            requests: [],
          };
        }
        clusters[key].count += 1;
        clusters[key].requests.push(r);
      });

    return Object.values(clusters).sort((a, b) => b.count - a.count);
  }, [requests]);

  // Handle Status Update
  const handleUpdateStatus = async (requestId: string, newStatus: DemandRequestStatus, tripId?: string) => {
    setActionLoading(true);
    const ok = await demandRequestService.updateDemandRequestStatus(requestId, newStatus, tripId);
    setActionLoading(false);

    if (ok) {
      setRequests((prev) =>
        prev.map((r) => (r.id === requestId ? { ...r, status: newStatus, matchedTripId: tripId || r.matchedTripId } : r))
      );
      setActionMessage({ text: 'تم تحديث حالة الطلب بنجاح', type: 'success' });
      setTimeout(() => setActionMessage(null), 3000);
      setSelectedReqForTrip(null);
    } else {
      setActionMessage({ text: 'تعذر تحديث الحالة، يرجى المحاولة مرة أخرى', type: 'error' });
      setTimeout(() => setActionMessage(null), 3000);
    }
  };

  // Handle Delete Request
  const handleDeleteRequest = async (requestId: string) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الطلب نهائياً من السجل؟')) return;
    setActionLoading(true);
    const ok = await demandRequestService.deleteDemandRequest(requestId);
    setActionLoading(false);
    if (ok) {
      setRequests((prev) => prev.filter((r) => r.id !== requestId));
      setActionMessage({ text: 'تم حذف الطلب بنجاح', type: 'success' });
      setTimeout(() => setActionMessage(null), 3000);
    } else {
      setActionMessage({ text: 'تعذر حذف الطلب', type: 'error' });
      setTimeout(() => setActionMessage(null), 3000);
    }
  };

  // Handle Seed Sample Data
  const handleSeedSamples = async () => {
    setActionLoading(true);
    try {
      const seeded = await demandRequestService.seedSampleDemandRequests();
      setRequests((prev) => [...seeded, ...prev]);
      setActionMessage({ text: `تم توليد ${seeded.length} طلبات تجريبية بنجاح لاختبار المنظومة!`, type: 'success' });
      setTimeout(() => setActionMessage(null), 4000);
    } catch (err: any) {
      setActionMessage({ text: 'حدث خطأ أثناء توليد البيانات التجريبية', type: 'error' });
      setTimeout(() => setActionMessage(null), 3000);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Create Request by Admin
  const handleCreateRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newStudentName.trim()) {
      alert('يرجى إدخال اسم الطالب');
      return;
    }
    const cleanPhone = newStudentPhone.replace(/[^0-9]/g, '');
    if (cleanPhone.length < 10) {
      alert('يرجى إدخال رقم هاتف صحيح');
      return;
    }

    const selectedUni = SEED_UNIVERSITIES.find((u) => u.id === newUniId) || SEED_UNIVERSITIES[0];

    setActionLoading(true);
    try {
      const res = await demandRequestService.submitDemandRequest({
        studentId: `admin_gen_${Date.now()}`,
        studentSnapshot: {
          fullName: newStudentName.trim(),
          phoneNumber: cleanPhone,
          email: `${cleanPhone}@otostudents.net`,
        },
        universityId: selectedUni.id,
        universityName: selectedUni.name,
        governorate: selectedUni.governorate,
        date: newDate,
        preferredDepartureTime: newDepartureTime,
        tripType: newTripType,
        pickupLocation: newPickupLocation,
        notes: newNotes,
      });

      if (res.success && res.data) {
        setRequests((prev) => [res.data!, ...prev]);
        setShowNewRequestModal(false);
        setNewStudentName('');
        setNewStudentPhone('');
        setNewNotes('');
        setActionMessage({ text: 'تم تسجيل رغبة السفر بنجاح وإضافتها للنظام!', type: 'success' });
        setTimeout(() => setActionMessage(null), 3000);
      } else {
        alert(res.error || 'تعذر تسجيل الطلب');
      }
    } catch (err: any) {
      alert('حدث خطأ أثناء حفظ الطلب: ' + (err?.message || 'خطأ غير معروف'));
    } finally {
      setActionLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(text);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <div className="admin-page-container" dir="rtl">
      {/* Header */}
      <div className="admin-page-header">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="admin-badge-purple">نظام قياس الطلب التشغيلي</span>
            <span className="text-xs text-slate-400 font-mono">Demand Heatmap & Aggregation</span>
          </div>
          <h2>طلبات الرحلات غير المجدولة والتشغيل التجميعي</h2>
          <p className="admin-page-subtitle">
            متابعة رغبات الطلاب في خطوط ومواعيد السفر غير المدرجة بجدول الرحلات، وتجميع الطلبات لجدولة حافلات سياحية جديدة (50 راكباً).
          </p>
        </div>

        <div className="admin-page-actions flex items-center gap-2 flex-wrap">
          {/* Link to Full Demand Matrix / Heatmap */}
          <button
            type="button"
            className="btn-admin-outline"
            onClick={() => navigate('/admin/student-demand')}
            title="الانتقال إلى خريطة الطلب والمصفوفة الجغرافية"
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <BarChart2 size={16} className="text-indigo-600" />
            <span>خريطة الطلب والمصفوفة</span>
          </button>

          {/* Refresh */}
          <button
            type="button"
            className="btn-admin-outline"
            onClick={loadRequests}
            disabled={isLoading || actionLoading}
            title="تحديث القائمة"
          >
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            <span>تحديث</span>
          </button>

          {/* Seed Sample Data (if empty or on demand) */}
          {requests.length === 0 && (
            <button
              type="button"
              className="btn-admin-outline"
              onClick={handleSeedSamples}
              disabled={actionLoading}
              style={{ borderColor: '#d97706', color: '#b45309', backgroundColor: '#fffbeb' }}
              title="توليد عينة رغبات لاختبار النظام"
            >
              <Sparkles size={16} />
              <span>توليد بيانات تجريبية</span>
            </button>
          )}

          {/* New Request Modal Button */}
          <button
            type="button"
            className="btn-admin-primary"
            onClick={() => setShowNewRequestModal(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <Plus size={16} />
            <span>تسجيل رغبة جديدة لطالب</span>
          </button>
        </div>
      </div>

      {actionMessage && (
        <div className={`admin-alert-banner ${actionMessage.type}`} style={{ marginBottom: '1.25rem' }}>
          {actionMessage.type === 'success' ? <CheckCircle2 size={18} /> : <XCircle size={18} />}
          <span>{actionMessage.text}</span>
        </div>
      )}

      {/* KPIs Grid */}
      <div className="admin-kpi-grid">
        <div className="admin-kpi-card">
          <div className="kpi-icon-box blue">
            <Send size={24} />
          </div>
          <div className="kpi-info">
            <span className="kpi-label">إجمالي الطلبات المسجلة</span>
            <div className="kpi-value-row">
              <span className="kpi-number">{stats.total}</span>
              <span className="kpi-unit">طلب مسجل</span>
            </div>
          </div>
        </div>

        <div className="admin-kpi-card highlight-kpi">
          <div className="kpi-icon-box orange">
            <Clock size={24} />
          </div>
          <div className="kpi-info">
            <span className="kpi-label">طلبات بانتظار اكتمال العدد</span>
            <div className="kpi-value-row">
              <span className="kpi-number">{stats.pending}</span>
              <span className="kpi-unit">طلب معلق</span>
            </div>
          </div>
        </div>

        <div className="admin-kpi-card">
          <div className="kpi-icon-box purple">
            <TrendingUp size={24} />
          </div>
          <div className="kpi-info">
            <span className="kpi-label">قيد الترتيب والتنسيق</span>
            <div className="kpi-value-row">
              <span className="kpi-number">{stats.reviewed}</span>
              <span className="kpi-unit">طلب منسق</span>
            </div>
          </div>
        </div>

        <div className="admin-kpi-card">
          <div className="kpi-icon-box green">
            <CheckCircle2 size={24} />
          </div>
          <div className="kpi-info">
            <span className="kpi-label">تم جدولة رحلات لها</span>
            <div className="kpi-value-row">
              <span className="kpi-number">{stats.created}</span>
              <span className="kpi-unit">طلب مجدول</span>
            </div>
          </div>
        </div>
      </div>

      {/* High Demand Clusters Section (Aggregation towards 50-passenger bus) */}
      {routeClusters.length > 0 && (
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '18px',
            border: '1px solid #e2e8f0',
            padding: '20px',
            marginBottom: '1.5rem',
            boxShadow: '0 2px 10px rgba(15, 23, 42, 0.03)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  backgroundColor: '#fef3c7',
                  color: '#d97706',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Sparkles size={18} />
              </div>
              <div>
                <h3 style={{ fontSize: '15px', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>
                  الخطوط ذات الإقبال المرتفع (تجميع الطلاب التشغيلي للحافلات)
                </h3>
                <span style={{ fontSize: '11px', color: '#64748b' }}>
                  تجميع طلبات الطلاب على نفس الوجهة واليوم لتحديد جدوى فتح خط حافلة سياحية (50 مقعداً)
                </span>
              </div>
            </div>

            <span
              style={{
                fontSize: '11px',
                fontWeight: 'bold',
                backgroundColor: '#f0fdf4',
                color: '#16a34a',
                padding: '4px 10px',
                borderRadius: '999px',
                border: '1px solid #bbf7d0',
              }}
            >
              سعة الحافلة السياحية: 50 مقعداً
            </span>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
              gap: '12px',
            }}
          >
            {routeClusters.slice(0, 6).map((c, idx) => {
              const capacityPercent = Math.min(100, Math.round((c.count / 50) * 100));
              const isHiAceReady = c.count >= 10;
              const isBusReady = c.count >= 30;

              return (
                <div
                  key={idx}
                  style={{
                    padding: '14px 16px',
                    borderRadius: '14px',
                    border: isBusReady
                      ? '1.5px solid #86efac'
                      : isHiAceReady
                      ? '1.5px solid #fed7aa'
                      : '1px solid #e2e8f0',
                    backgroundColor: isBusReady ? '#f0fdf4' : isHiAceReady ? '#fffbeb' : '#f8fafc',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div>
                      <strong style={{ fontSize: '13px', color: '#0f172a', display: 'block' }}>
                        {c.university}
                      </strong>
                      <span style={{ fontSize: '11px', color: '#64748b', fontFamily: 'monospace' }}>
                        تاريخ السفر: {c.date}
                      </span>
                    </div>

                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: 'bold',
                        padding: '2px 8px',
                        borderRadius: '6px',
                        backgroundColor: isBusReady ? '#dcfce7' : '#fee2e2',
                        color: isBusReady ? '#15803d' : '#991b1b',
                      }}
                    >
                      {c.count} طالب مجمع
                    </span>
                  </div>

                  {/* Progress towards 50 seats */}
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#64748b', marginBottom: '3px' }}>
                      <span>نسبة امتلاء الحافلة ({capacityPercent}%)</span>
                      <span>{c.count} / 50 مقعد</span>
                    </div>
                    <div style={{ width: '100%', height: '7px', backgroundColor: '#e2e8f0', borderRadius: '999px', overflow: 'hidden' }}>
                      <div
                        style={{
                          height: '100%',
                          backgroundColor: isBusReady ? '#16a34a' : isHiAceReady ? '#f59e0b' : '#E5232A',
                          width: `${capacityPercent}%`,
                          borderRadius: '999px',
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '8px', borderTop: '1px solid rgba(0,0,0,0.05)', fontSize: '11px' }}>
                    <span style={{ color: isBusReady ? '#15803d' : isHiAceReady ? '#b45309' : '#64748b', fontWeight: 'bold' }}>
                      {isBusReady ? 'جاهزة لحافلة سياحية 50 راكب ✅' : isHiAceReady ? 'جاهزة لميكروباص 14 راكب 🚐' : `متبقي ${14 - c.count} لاكتمال ميكروباص`}
                    </span>
                    <button
                      type="button"
                      onClick={() => navigate('/admin/trips')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        border: 'none',
                        backgroundColor: '#ffffff',
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        borderColor: '#cbd5e1',
                        borderRadius: '6px',
                        padding: '4px 8px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        color: '#334155',
                        cursor: 'pointer',
                      }}
                    >
                      <span>جدولة الرحلة</span>
                      <ArrowRight size={11} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* FIXED TOOLBAR AND FILTER BAR (Using exact AdminPages.css classes) */}
      <div className="admin-toolbar-card" style={{ marginBottom: '1.25rem' }}>
        {/* Search Input */}
        <div className="admin-search-wrapper">
          <Search size={18} />
          <input
            type="text"
            className="admin-search-input"
            placeholder="بحث بكود الطلب، اسم الطالب، الهاتف، الجامعة، أو نقطة التجمع..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#94a3b8',
                fontSize: '16px',
              }}
            >
              ×
            </button>
          )}
        </div>

        {/* Filter Group */}
        <div className="admin-filter-group">
          {/* Status Filter */}
          <select
            className="admin-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="ALL">جميع الحالات ({requests.length})</option>
            <option value="PENDING">معلق (بانتظار العدد)</option>
            <option value="REVIEWED">قيد الترتيب والتنسيق</option>
            <option value="TRIP_CREATED">تم جدولة رحلة</option>
            <option value="REJECTED">معتذر عنه</option>
          </select>

          {/* University Destination Filter */}
          <select
            className="admin-filter-select"
            value={uniFilter}
            onChange={(e) => setUniFilter(e.target.value)}
          >
            <option value="ALL">جميع الجامعات ({universitiesList.length})</option>
            {universitiesList.map((uni) => (
              <option key={uni} value={uni}>
                {uni}
              </option>
            ))}
          </select>

          {/* Trip Type Filter */}
          <select
            className="admin-filter-select"
            value={tripTypeFilter}
            onChange={(e) => setTripTypeFilter(e.target.value)}
          >
            <option value="ALL">كافة أنواع الرحلات</option>
            <option value="ROUND_TRIP">ذهاب وعودة 🔄</option>
            <option value="ONE_WAY">ذهاب فقط ☀️</option>
          </select>

          {/* Reset Filters Button */}
          {(searchTerm || statusFilter !== 'ALL' || uniFilter !== 'ALL' || tripTypeFilter !== 'ALL') && (
            <button
              type="button"
              className="btn-admin-outline"
              style={{ padding: '0.65rem 0.85rem', fontSize: '0.82rem' }}
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('ALL');
                setUniFilter('ALL');
                setTripTypeFilter('ALL');
              }}
            >
              إلغاء الفلاتر
            </button>
          )}
        </div>
      </div>

      {/* Requests Data Table */}
      <div className="admin-table-container">
        {isLoading ? (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: '#64748b' }}>
            <RefreshCw size={32} className="animate-spin" style={{ margin: '0 auto 12px', opacity: 0.5 }} />
            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>جارٍ تحميل طلبات الرحلات غير المجدولة...</span>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: '#64748b' }}>
            <Send size={44} style={{ margin: '0 auto 14px', color: '#cbd5e1' }} />
            <h4 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1e293b', marginBottom: '6px' }}>
              لا توجد طلبات رحلات مطابقة للفلاتر
            </h4>
            <p style={{ fontSize: '13px', color: '#64748b', maxWidth: '420px', margin: '0 auto 18px' }}>
              {requests.length === 0
                ? 'لم يسجل الطلاب أي رغبات سفر غير مجدولة حتى الآن. يمكنك تسجيل رغبة لطالب يدوياً أو توليد بيانات تجريبية فوراً لاختبار كافة وظائف المنظومة.'
                : 'جرب تغيير مصطلحات البحث أو إعادة تعيين الفلاتر لرؤية النتائج.'}
            </p>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn-admin-primary"
                onClick={() => setShowNewRequestModal(true)}
              >
                <Plus size={16} />
                <span>+ تسجيل رغبة جديدة لطالب</span>
              </button>

              {requests.length === 0 && (
                <button
                  type="button"
                  className="btn-admin-outline"
                  onClick={handleSeedSamples}
                  disabled={actionLoading}
                >
                  <Sparkles size={16} />
                  <span>توليد بيانات تجريبية للاختبار</span>
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            <table className="admin-data-table">
            <thead>
              <tr>
                <th>كود الطلب</th>
                <th>بيانات الطالب</th>
                <th>الجامعة الوجهة</th>
                <th>تاريخ السفر</th>
                <th>الموعد والنوع</th>
                <th>نقطة الركوب والملاحظات</th>
                <th>الحالة التشغيلية</th>
                <th style={{ textAlign: 'center' }}>الإجراءات والتواصل</th>
              </tr>
            </thead>
            <tbody>
              {visibleRequests.map((req) => {
                const isCreated = req.status === 'TRIP_CREATED';
                const isPending = req.status === 'PENDING';
                const isReviewed = req.status === 'REVIEWED';
                const isRejected = req.status === 'REJECTED';

                const cleanPhone = (req.studentSnapshot?.phoneNumber || '').replace(/[^0-9]/g, '');
                const waNumber = cleanPhone.startsWith('0') ? '2' + cleanPhone : cleanPhone;

                const waMessage = `مرحباً ${req.studentSnapshot?.fullName}، بخصوص رغبتك المحجوزة برقم (${req.requestCode}) للسفر إلى ${req.universityName} يوم ${req.date} عبر منصة OTO STUDENTS...`;

                return (
                  <tr key={req.id}>
                    {/* Request Code */}
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span
                          style={{
                            fontFamily: 'monospace',
                            fontWeight: 'bold',
                            color: '#0f172a',
                            fontSize: '12px',
                            backgroundColor: '#f1f5f9',
                            padding: '3px 6px',
                            borderRadius: '6px',
                          }}
                        >
                          {req.requestCode}
                        </span>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(req.requestCode)}
                          style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', padding: '2px' }}
                          title="نسخ كود الطلب"
                        >
                          {copiedCode === req.requestCode ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
                        </button>
                      </div>
                      <span style={{ fontSize: '10px', color: '#94a3b8', display: 'block', marginTop: '2px' }}>
                        {req.createdAt ? new Date(req.createdAt).toLocaleDateString('ar-EG') : ''}
                      </span>
                    </td>

                    {/* Student Info */}
                    <td>
                      <div>
                        <strong style={{ display: 'block', color: '#0f172a', fontSize: '13px', marginBottom: '2px' }}>
                          {req.studentSnapshot?.fullName || 'طالب مسجل'}
                        </strong>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: '#475569' }}>
                          <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }} dir="ltr">
                            {req.studentSnapshot?.phoneNumber || '—'}
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* University & Governorate */}
                    <td>
                      <div>
                        <strong style={{ display: 'block', color: '#0f172a', fontSize: '13px' }}>
                          {req.universityName}
                        </strong>
                        <span style={{ fontSize: '11px', color: '#64748b' }}>
                          {req.governorate || 'وجهة جامعية'}
                        </span>
                      </div>
                    </td>

                    {/* Travel Date */}
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Calendar size={13} className="text-[#E5232A]" />
                        <span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#0f172a', fontSize: '12px' }}>
                          {req.date}
                        </span>
                      </div>
                    </td>

                    {/* Departure Time & Trip Type */}
                    <td>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 'bold', color: '#0f172a' }}>
                          <Clock size={12} className="text-slate-500" />
                          <span style={{ fontFamily: 'monospace' }}>{req.preferredDepartureTime}</span>
                        </div>
                        <span
                          style={{
                            fontSize: '10px',
                            fontWeight: 'bold',
                            display: 'inline-block',
                            marginTop: '2px',
                            color: req.tripType === 'ROUND_TRIP' ? '#7c3aed' : '#0284c7',
                            backgroundColor: req.tripType === 'ROUND_TRIP' ? '#f5f3ff' : '#f0f9ff',
                            padding: '1px 5px',
                            borderRadius: '4px',
                          }}
                        >
                          {req.tripType === 'ROUND_TRIP' ? 'ذهاب وعودة 🔄' : 'ذهاب فقط ☀️'}
                        </span>
                      </div>
                    </td>

                    {/* Pickup Point & Notes */}
                    <td>
                      <div style={{ maxWidth: '240px', fontSize: '11px' }}>
                        {req.pickupLocation && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#334155', fontWeight: 'bold', marginBottom: '2px' }}>
                            <MapPin size={11} className="text-[#E5232A] flex-shrink-0" />
                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {req.pickupLocation}
                            </span>
                          </div>
                        )}
                        <span style={{ color: '#64748b', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={req.notes}>
                          {req.notes || '— بدون ملاحظات إضافية —'}
                        </span>
                      </div>
                    </td>

                    {/* Status Badge */}
                    <td>
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: 'bold',
                          padding: '3px 8px',
                          borderRadius: '6px',
                          display: 'inline-block',
                          backgroundColor: isCreated
                            ? '#dcfce7'
                            : isReviewed
                            ? '#f3e8ff'
                            : isRejected
                            ? '#fee2e2'
                            : '#fef3c7',
                          color: isCreated
                            ? '#15803d'
                            : isReviewed
                            ? '#6b21a8'
                            : isRejected
                            ? '#991b1b'
                            : '#92400e',
                        }}
                      >
                        {isCreated
                          ? 'تم جدولة رحلة ✓'
                          : isReviewed
                          ? 'قيد الترتيب 📋'
                          : isRejected
                          ? 'معتذر عنه ✕'
                          : 'معلق (انتظار العدد) ⏳'}
                      </span>
                      {req.matchedTripId && (
                        <span style={{ fontSize: '9px', color: '#16a34a', display: 'block', marginTop: '2px', fontFamily: 'monospace' }}>
                          رحلة: {req.matchedTripId}
                        </span>
                      )}
                    </td>

                    {/* Actions & Communication */}
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                        {/* WhatsApp Contact */}
                        {cleanPhone && (
                          <a
                            href={`https://wa.me/${waNumber}?text=${encodeURIComponent(waMessage)}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              width: '30px',
                              height: '30px',
                              borderRadius: '8px',
                              backgroundColor: '#dcfce7',
                              color: '#15803d',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              textDecoration: 'none',
                            }}
                            title="تواصل واتساب فوري"
                          >
                            <MessageSquare size={14} />
                          </a>
                        )}

                        {/* Phone Call */}
                        {cleanPhone && (
                          <a
                            href={`tel:${cleanPhone}`}
                            style={{
                              width: '30px',
                              height: '30px',
                              borderRadius: '8px',
                              backgroundColor: '#e0f2fe',
                              color: '#0369a1',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              textDecoration: 'none',
                            }}
                            title="اتصال هاتفي"
                          >
                            <Phone size={14} />
                          </a>
                        )}

                        {/* Action: Mark as Reviewed */}
                        {isPending && (
                          <button
                            type="button"
                            onClick={() => handleUpdateStatus(req.id, 'REVIEWED')}
                            disabled={actionLoading}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '6px',
                              border: '1px solid #c084fc',
                              backgroundColor: '#faf5ff',
                              color: '#7e22ce',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              cursor: 'pointer',
                            }}
                            title="نقل لحالة قيد الترتيب"
                          >
                            ترتيب
                          </button>
                        )}

                        {/* Action: Link to Trip / Mark Created */}
                        {!isCreated && (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedReqForTrip(req);
                              setTripCodeInput('');
                            }}
                            disabled={actionLoading}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 8px',
                              borderRadius: '6px',
                              border: '1px solid #86efac',
                              backgroundColor: '#f0fdf4',
                              color: '#15803d',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              cursor: 'pointer',
                            }}
                            title="اعتماد وجدولة رحلة لهذا الطلب"
                          >
                            <CheckCircle2 size={12} />
                            <span>جدولة</span>
                          </button>
                        )}

                        {/* Action: Reject */}
                        {!isRejected && (
                          <button
                            type="button"
                            onClick={() => handleUpdateStatus(req.id, 'REJECTED')}
                            disabled={actionLoading}
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '6px',
                              border: '1px solid #fecaca',
                              backgroundColor: '#fff5f5',
                              color: '#dc2626',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                            }}
                            title="اعتذار عن الطلب"
                          >
                            <XCircle size={14} />
                          </button>
                        )}

                        {/* Action: Delete */}
                        <button
                          type="button"
                          onClick={() => handleDeleteRequest(req.id)}
                          disabled={actionLoading}
                          style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '6px',
                            border: '1px solid #e2e8f0',
                            backgroundColor: '#ffffff',
                            color: '#94a3b8',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                          }}
                          title="حذف من السجل"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

            {filteredRequests.length > 0 && (
              <AdminLoadMore
                currentCount={Math.min(visibleCount, filteredRequests.length)}
                totalCount={filteredRequests.length}
                itemName="طلب رغبة سفر"
                onLoadMore={() => setVisibleCount((prev) => prev + 10)}
                step={10}
                onShowAll={() => setVisibleCount(filteredRequests.length)}
              />
            )}
          </>
        )}
      </div>

      {/* MODAL 1: LINK TO TRIP MODAL */}
      {selectedReqForTrip && (
        <div className="admin-modal-overlay" onClick={() => setSelectedReqForTrip(null)}>
          <div
            className="admin-modal-card"
            style={{ maxWidth: '480px', borderRadius: '20px', padding: '24px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CheckCircle2 size={20} className="text-emerald-600" />
                <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>
                  اعتماد وجدولة طلب الرحلة
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedReqForTrip(null)}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={18} />
              </button>
            </div>

            <p style={{ fontSize: '13px', color: '#475569', marginBottom: '16px' }}>
              سيتم تغيير حالة الطلب إلى <strong style={{ color: '#16a34a' }}>تمت الجدولة (TRIP_CREATED)</strong> وربطه بالرحلة بالمنظومة.
            </p>

            <div style={{ backgroundColor: '#f8fafc', padding: '14px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '16px', fontSize: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ color: '#64748b' }}>الطالب:</span>
                <strong style={{ color: '#0f172a' }}>{selectedReqForTrip.studentSnapshot?.fullName}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ color: '#64748b' }}>الوجهة:</span>
                <strong style={{ color: '#0f172a' }}>{selectedReqForTrip.universityName}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>تاريخ السفر:</span>
                <strong style={{ color: '#0f172a', fontFamily: 'monospace' }}>{selectedReqForTrip.date}</strong>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#334155', display: 'block', marginBottom: '6px' }}>
                كود أو معرّف الرحلة المجدولة في جدول المواعيد (اختياري):
              </label>
              <input
                type="text"
                className="admin-search-input"
                style={{ height: '40px', padding: '0 12px' }}
                placeholder="مثال: TRIP-104 أو كود الرحلة..."
                value={tripCodeInput}
                onChange={(e) => setTripCodeInput(e.target.value)}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                className="btn-admin-outline"
                style={{ flex: 1, height: '42px' }}
                onClick={() => setSelectedReqForTrip(null)}
              >
                إلغاء
              </button>
              <button
                type="button"
                className="btn-admin-primary"
                style={{ flex: 2, height: '42px', backgroundColor: '#16a34a', borderColor: '#16a34a' }}
                onClick={() => handleUpdateStatus(selectedReqForTrip.id, 'TRIP_CREATED', tripCodeInput)}
                disabled={actionLoading}
              >
                <span>اعتماد وجدولة الرحلة</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 2: NEW DEMAND REQUEST MODAL (Admin Entry) */}
      {showNewRequestModal && (
        <div className="admin-modal-overlay" onClick={() => setShowNewRequestModal(false)}>
          <div
            className="admin-modal-card"
            style={{ maxWidth: '560px', borderRadius: '22px', padding: '24px', overflowY: 'auto', maxHeight: '90vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', backgroundColor: '#FFF1F2', color: '#E5232A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Plus size={20} />
                </div>
                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>
                    تسجيل رغبة سفر غير مدرجة لطالب
                  </h3>
                  <span style={{ fontSize: '11px', color: '#64748b' }}>
                    تسجيل طلب خط أو موعد سفر جديد يدخل في تجميع إقبال الطلاب
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowNewRequestModal(false)}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateRequest} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {/* Student Name & Phone */}
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#334155', display: 'block', marginBottom: '4px' }}>
                    اسم الطالب الثلاثي: *
                  </label>
                  <input
                    type="text"
                    required
                    className="admin-search-input"
                    style={{ height: '38px', padding: '0 12px' }}
                    placeholder="مثال: يوسف حسام الدين"
                    value={newStudentName}
                    onChange={(e) => setNewStudentName(e.target.value)}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#334155', display: 'block', marginBottom: '4px' }}>
                    رقم الهاتف المحمول: *
                  </label>
                  <input
                    type="tel"
                    required
                    className="admin-search-input"
                    style={{ height: '38px', padding: '0 12px' }}
                    placeholder="01012345678"
                    value={newStudentPhone}
                    onChange={(e) => setNewStudentPhone(e.target.value)}
                  />
                </div>
              </div>

              {/* Destination University & Travel Date */}
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#334155', display: 'block', marginBottom: '4px' }}>
                    الجامعة الوجهة: *
                  </label>
                  <select
                    className="admin-filter-select w-full"
                    style={{ height: '38px', fontSize: '12px' }}
                    value={newUniId}
                    onChange={(e) => setNewUniId(e.target.value)}
                  >
                    {SEED_UNIVERSITIES.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.governorate})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#334155', display: 'block', marginBottom: '4px' }}>
                    تاريخ السفر المطلوب: *
                  </label>
                  <input
                    type="date"
                    required
                    className="admin-search-input"
                    style={{ height: '38px', padding: '0 12px' }}
                    value={newDate}
                    onChange={(e) => setNewDate(e.target.value)}
                  />
                </div>
              </div>

              {/* Trip Type & Preferred Time */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#334155', display: 'block', marginBottom: '4px' }}>
                    نوع الرحلة:
                  </label>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      backgroundColor: '#e2e8f0',
                      borderRadius: '8px',
                      padding: '2px',
                      gap: '2px',
                      height: '38px',
                      boxSizing: 'border-box',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setNewTripType('ROUND_TRIP')}
                      style={{
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        backgroundColor: newTripType === 'ROUND_TRIP' ? '#ffffff' : 'transparent',
                        color: newTripType === 'ROUND_TRIP' ? '#E5232A' : '#64748b',
                      }}
                    >
                      ذهاب وعودة 🔄
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewTripType('ONE_WAY')}
                      style={{
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        backgroundColor: newTripType === 'ONE_WAY' ? '#ffffff' : 'transparent',
                        color: newTripType === 'ONE_WAY' ? '#E5232A' : '#64748b',
                      }}
                    >
                      ذهاب فقط ☀️
                    </button>
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#334155', display: 'block', marginBottom: '4px' }}>
                    توقيت التحرك المفضل:
                  </label>
                  <input
                    type="time"
                    className="admin-search-input"
                    style={{ height: '38px', padding: '0 12px' }}
                    value={newDepartureTime}
                    onChange={(e) => setNewDepartureTime(e.target.value)}
                  />
                </div>
              </div>

              {/* Alexandria Pickup Point */}
              <div>
                <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#334155', display: 'block', marginBottom: '4px' }}>
                  نقطة التجمع والركوب بالإسكندرية:
                </label>
                <select
                  className="admin-filter-select w-full"
                  style={{ height: '38px', fontSize: '12px' }}
                  value={newPickupLocation}
                  onChange={(e) => setNewPickupLocation(e.target.value)}
                >
                  {ALEXANDRIA_PICKUP_POINTS.map((pt) => (
                    <option key={pt} value={pt}>
                      {pt}
                    </option>
                  ))}
                </select>
              </div>

              {/* Notes */}
              <div>
                <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#334155', display: 'block', marginBottom: '4px' }}>
                  ملاحظات أو مواعيد خاصة:
                </label>
                <input
                  type="text"
                  className="admin-search-input"
                  style={{ height: '38px', padding: '0 12px' }}
                  placeholder="مثال: يفضل العودة 04:30 عصراً، كلية الهندسة..."
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '6px' }}>
                <button
                  type="button"
                  className="btn-admin-outline"
                  style={{ flex: 1, height: '42px' }}
                  onClick={() => setShowNewRequestModal(false)}
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="btn-admin-primary"
                  style={{ flex: 2, height: '42px' }}
                  disabled={actionLoading}
                >
                  <span>{actionLoading ? 'جاري الحفظ...' : 'تسجيل الرغبة وحفظها'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
export default AdminDemandRequests;
